import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import { postJSONPS } from '../../lib/utils/fetch';
import { composePS } from '@cd/pipe';
import styles from '../css/Login.module.scss';
import { either } from '../../lib/utils/utils';
import { CircularProgress } from '@mui/material';
import { postGetStaticInfo } from '../../postGetStatic';
import postGetServerVar from './../../getServerVars';
import withAlert from './withAlert';
import CenterProgress from '../../CenterProgress';
import { VERSION } from '../../VERSION';
import Typography from '@mui/material/Typography';
import TextFieldCD from '../../dynamic/components/form/components/TextFieldCD';

/** postLogin :: string -> string  -> Promise(Either) */
const postLogin = (user, pwd, version) =>
	composePS(postJSONPS)('POST', `/login`, {
		body: JSON.stringify({ user, pwd, version })
	});

/** postVerify2fa :: string -> Promise(Either) */
const postVerify2fa = code =>
	composePS(postJSONPS)('POST', `/login/verify2fa`, {
		body: JSON.stringify({ code })
	});

/** postResend2fa :: -> Promise(Either) */
const postResend2fa = () =>
	composePS(postJSONPS)('POST', `/login/resend2fa`, {
		body: JSON.stringify({})
	});

const Login = withAlert(({ onLogin: onLoginSuccess, onOpenAlertDlg }) => {
	const version = VERSION;
	const [user, setUser] = useState('');
	const [pwd, setPwd] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [twoFAStep, setTwoFAStep] = useState(false);
	const [code, setCode] = useState('');
	const [logInProgress, setLogInProgress] = useState(undefined);
	const [loginText, setLoginText] = useState(undefined);
	const [loginRegistrationButton, setLoginRegistrationButton] =
		useState(undefined);

	useEffect(() => {
		postGetStaticInfo('signInMsg').then(
			either(console.log, t => setLoginText(t))
		);

		postGetServerVar('loginRegistrationButton').then(
			either(console.log, b => setLoginRegistrationButton(b))
		);
	}, []);

	const onShowPassword = () => setShowPassword(!showPassword);
	const onUserChange = v => setUser(v.target.value);
	const onPwdChange = v => setPwd(v.target.value);

	const onLogin = () => {
		setLogInProgress(true);
		postLogin(user, pwd, version).then(
			either(
				e => {
					setLogInProgress(false);
					onOpenAlertDlg({
						message: "Can't login in." + (!e ? 'Network Error' : e),
						title: 'Error'
					});
				},
				e => {
					setLogInProgress(false);
					// '2FA' -> show the code-entry step; otherwise the
					// usual 'OK' completes sign-in.
					if (e === '2FA') {
						setTwoFAStep(true);
					} else {
						onLoginSuccess(e);
					}
				}
			)
		);
	};

	const onEnter = e => {
		if (e.key === 'Enter') {
			onLogin();
		}
	};

	const onCodeChange = v => setCode(v.target.value);

	const onVerify2fa = () => {
		setLogInProgress(true);
		postVerify2fa(code).then(
			either(
				e => {
					setLogInProgress(false);
					onOpenAlertDlg({
						message: !e ? 'Network Error' : e,
						title: 'Error'
					});
				},
				e => {
					setLogInProgress(false);
					onLoginSuccess(e);
				}
			)
		);
	};

	const onResend2fa = () => {
		setLogInProgress(true);
		postResend2fa().then(
			either(
				e => {
					setLogInProgress(false);
					onOpenAlertDlg({
						message: !e ? 'Network Error' : e,
						title: 'Error'
					});
				},
				() => {
					setLogInProgress(false);
					onOpenAlertDlg({
						message: 'A new code has been sent to your email.',
						title: 'Code sent'
					});
				}
			)
		);
	};

	const onCancel2fa = () => {
		setTwoFAStep(false);
		setCode('');
	};

	if (!loginText) {
		return <CenterProgress />;
	}

	return (
		<div className={styles.wrap}>
			<div className={styles.loginText}>{loginText}</div>
			<div className={styles.island}>
				{twoFAStep ? (
					<>
						<div className={styles.textFields}>
							<div className={styles.title}>Verify</div>
							<Typography variant="body2" sx={{ mb: 1 }}>
								We emailed a verification code. Enter it below to
								finish signing in.
							</Typography>
							<TextFieldCD
								id="code"
								name="code"
								type="text"
								label="Verification code"
								margin="normal"
								onUpdateData={onCodeChange}
								value={code}
								data={{ code: code }}
								validation={{}}
								inputProps={{
									inputMode: 'numeric',
									autoComplete: 'one-time-code'
								}}
								onKeyPress={e =>
									e.key === 'Enter' && code && onVerify2fa()
								}
							/>
						</div>
						<Button
							disabled={!code || logInProgress}
							color="primary"
							variant="contained"
							onClick={onVerify2fa}
							type="submit">
							Verify &amp; Sign In
						</Button>
						<Button
							disabled={logInProgress}
							color="primary"
							onClick={onResend2fa}>
							Resend code
						</Button>
						<Button disabled={logInProgress} onClick={onCancel2fa}>
							Start over
						</Button>
					</>
				) : (
					<>
						<div className={styles.textFields}>
							<div className={styles.title}>Login</div>
							<TextFieldCD
								id="user"
								name="user"
								type="text"
								label="Email"
								margin="normal"
								onUpdateData={onUserChange}
								value={user}
								data={{ user: user }}
								validation={{}}
								inputProps={{}}
								onKeyPress={onEnter}
							/>
							<TextFieldCD
								id="password"
								name="password"
								type="p"
								label="Password"
								margin="normal"
								onKeyPress={onEnter}
								validation={{}}
								inputProps={{}}
								data={{ password: pwd }}
								onUpdateData={onPwdChange}
								showPassword={showPassword}
								onShowPassword={onShowPassword}
							/>
							<Link
								className={styles.link + ' ' + styles.alignSelf}
								to="/forgotPassword">
								Forgot Password?
							</Link>
						</div>
						<Button
							disabled={!user || !pwd || logInProgress}
							color="primary"
							variant="contained"
							onClick={onLogin}
							type="submit">
							Login Now
						</Button>
						{(loginRegistrationButton & 2) !== 0 && (
							<div className={styles.registrationUnderLogin}>
								<Typography
									color="primary"
									variant="subtitle1">
									Don't have an account?
								</Typography>

								<Link className={styles.link} to="/register">
									Register Now!
								</Link>
							</div>
						)}
					</>
				)}
				{logInProgress ? (
					<div className={styles.progressWrap}>
						<CircularProgress size={48} />
					</div>
				) : null}
			</div>
		</div>
	);
});

export default Login;
